import {useState} from 'react';
import {SoftCard} from './ui/modern-card';
import {Button} from './ui/button';
import {Separator} from './ui/separator';
import {Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter} from './ui/dialog';
import {ArrowLeft, MapPin, Clock, ExternalLink, Share2, Trash2, CheckCircle, AlertCircle, AlertTriangle} from 'lucide-react';
import {ImageWithFallback} from './figma/ImageWithFallback';
import {GoogleMap} from '@react-google-maps/api';
import {useAuth} from '../hooks/useAuth';
import {useToast} from '../contexts/ToastContext';

interface Report {
  id: string;
  title: string;
  date: string;
  location: string;
  latitude: number;
  longitude: number;
  status?: 'in_progress' | 'done';
  description?: string;
  photo?: string;
  photoUrl?: string;
  userId?: string;
}

interface ReportDetailProps {
  report: Report;
  onBack?: () => void;
  showBackButton?: boolean;
  onGenerateLink?: (reportId: string) => string;
  onDelete?: (reportId: string) => void;
  onUpdateStatus?: (reportId: string, status: 'in_progress' | 'done') => void;
}

export default function ReportDetail({report, onBack, showBackButton = false, onGenerateLink, onDelete, onUpdateStatus}: ReportDetailProps) {
  const {user, isAdmin, isZoneManager} = useAuth();
  const toast = useToast();
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isPhotoFullscreenOpen, setIsPhotoFullscreenOpen] = useState(false);



  // Fonction pour copier le lien public
  const handleCopyLink = async () => {
    if (!onGenerateLink) return;

    const link = onGenerateLink(report.id);

    try {
      await navigator.clipboard.writeText(link);
      toast.success('Lien copié', 'Le lien de partage a été copié dans le presse-papiers');
    } catch (error) {
      console.error('Erreur lors de la copie:', error);
      // Fallback pour les navigateurs anciens
      try {
        const textArea = document.createElement('textarea');
        textArea.value = link;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        toast.success('Lien copié', 'Le lien de partage a été copié dans le presse-papiers');
      } catch (fallbackError) {
        toast.error('Erreur de copie', 'Impossible de copier le lien. Copiez manuellement l\'URL de la page.');
      }
    }
  };

  // Fonction pour supprimer le signalement
  const handleDelete = async () => {
    if (!onDelete || !isAdmin) return;
    setIsDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!onDelete) return;

    try {
      await onDelete(report.id);
      toast.success('Signalement supprimé', 'Le signalement a été supprimé avec succès');
      setIsDeleteConfirmOpen(false);
      if (onBack) onBack();
    } catch (error) {
      toast.error('Erreur', 'Impossible de supprimer le signalement');
      setIsDeleteConfirmOpen(false);
    }
  };

  const handleStatusChange = async (newStatus?: 'in_progress' | 'done') => {
    if (!onUpdateStatus || (!isAdmin && !isZoneManager)) return;

    // Si pas de statut spécifié, toggle
    const targetStatus = newStatus || (report.status === 'in_progress' ? 'done' : 'in_progress');

    try {
      await onUpdateStatus(report.id, targetStatus);
      // Le toast est géré dans App.tsx, pas besoin de le dupliquer ici
    } catch (error) {
      toast.error('Erreur', 'Impossible de modifier le statut');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };


  const handleMapClick = () => {
    const mapUrl = `https://www.google.com/maps/search/?api=1&query=${report.latitude},${report.longitude}`;
    window.open(mapUrl, '_blank');
  };

  const formattedDate = formatDate(report.date);

  return (
    <div className="space-y-6">
      {/* Header - seulement si showBackButton */}
      {showBackButton && (
        <div className="bg-white/95 backdrop-blur-sm border-b border-gray-100/50 sticky top-0 z-10">
          <div className="px-4 sm:px-6 py-4">
            <div className="flex items-center gap-4">
              {onBack && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onBack}
                  className="shrink-0"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Retour
                </Button>
              )}
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-primary rounded-md flex items-center justify-center">
                  <span className="text-white text-sm">🐗</span>
                </div>
                <h1 className="text-lg font-medium">Détail du signalement</h1>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Titre pour mode modale */}
      {!showBackButton && (
        <div className="px-6 pt-6 pb-2">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <span className="text-white text-base">🐗</span>
            </div>
            <h1 className="text-xl font-medium">Détail du signalement</h1>
          </div>
        </div>
      )}

      {/* Contenu principal */}
      <div className={`space-y-6 ${!showBackButton ? 'px-6 pb-6' : 'px-4 sm:px-6 pb-6'}`}>
        {/* Informations principales */}
        <SoftCard className="p-6">
          <div className="space-y-4">
            {/* En-tête */}
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="w-4 h-4" />
                    <span>Date du signalement : {formattedDate}</span>
                  </div>
                  {report.userId === user?.id && (
                    <div className="text-sm text-blue-600">Signalement créé par vous</div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {onGenerateLink && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCopyLink}
                      className="gap-2"
                      title="Copier le lien de partage"
                    >
                      <Share2 className="w-4 h-4" />
                      Partager
                    </Button>
                  )}
                  {isAdmin && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleDelete}
                      className="gap-2 bg-red-600 hover:bg-red-700 text-white"
                      title="Supprimer ce signalement"
                    >
                      <Trash2 className="w-4 h-4" />
                      Supprimer
                    </Button>
                  )}
                </div>
              </div>

              {/* Section Statut avec chips cliquables - BLOC ALIGNÉ À DROITE */}
              <div className="flex justify-end">
                <div className="space-y-2 text-left">
                  <h3 className="text-sm font-medium text-gray-900">Statut</h3>
                  <div className="flex items-center gap-2">
                    {/* Chip En cours - PLUS PETITE */}
                    <button
                      onClick={() => (isAdmin || isZoneManager) && onUpdateStatus && handleStatusChange('in_progress')}
                      disabled={!(isAdmin || isZoneManager) || !onUpdateStatus}
                      className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-all ${report.status === 'in_progress'
                        ? 'bg-orange-100 text-orange-800 border-2 border-orange-300'
                        : 'bg-gray-100 text-gray-500 border border-gray-200 opacity-60'
                        } ${(isAdmin || isZoneManager) && onUpdateStatus ? 'hover:opacity-80 cursor-pointer' : 'cursor-default'}`}
                    >
                      <AlertCircle className="w-3 h-3" />
                      En cours
                    </button>

                    {/* Chip Terminé - PLUS PETITE */}
                    <button
                      onClick={() => (isAdmin || isZoneManager) && onUpdateStatus && handleStatusChange('done')}
                      disabled={!(isAdmin || isZoneManager) || !onUpdateStatus}
                      className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-all ${report.status === 'done'
                        ? 'bg-green-100 text-green-800 border-2 border-green-300'
                        : 'bg-gray-100 text-gray-500 border border-gray-200 opacity-60'
                        } ${(isAdmin || isZoneManager) && onUpdateStatus ? 'hover:opacity-80 cursor-pointer' : 'cursor-default'}`}
                    >
                      <CheckCircle className="w-3 h-3" />
                      Terminé
                    </button>
                  </div>
                  {(isAdmin || isZoneManager) && onUpdateStatus && (
                    <p className="text-xs text-gray-500">Cliquez sur un statut pour le modifier</p>
                  )}
                </div>
              </div>
            </div>

            <Separator />

            {/* Description */}
            <div className="space-y-2">
              <h3 className="font-medium">Description</h3>
              <p className="text-muted-foreground leading-relaxed">
                {report.description || report.title}
              </p>
            </div>

            {/* Photo si présente - CLIQUABLE */}
            {report.photoUrl && (
              <>
                <Separator />
                <div className="space-y-3">
                  <h3 className="font-medium">Photo</h3>
                  <div className="relative">
                    <ImageWithFallback
                      src={report.photoUrl}
                      alt="Photo du signalement"
                      className="w-full max-w-md rounded-lg border border-gray-100/50 shadow-sm cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => setIsPhotoFullscreenOpen(true)}
                    />
                    <div className="absolute top-2 right-2 bg-black/50 text-white px-2 py-1 rounded text-xs">
                      Cliquez pour agrandir
                    </div>
                  </div>
                </div>
              </>
            )}

            <Separator />

            {/* Localisation */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-muted-foreground" />
                <h3 className="font-medium">Localisation</h3>
              </div>
              <div className="space-y-2">
                <p className="text-sm">{report.location}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleMapClick}
                  className="gap-2"
                >
                  <ExternalLink className="w-3 h-3" />
                  Ouvrir dans Google Maps
                </Button>
              </div>
            </div>
          </div>
        </SoftCard>

        {/* Mini-carte */}
        <SoftCard className="p-6">
          <div className="space-y-4">
            <h3 className="font-medium">Position sur la carte</h3>
            <div className="relative w-full h-48 sm:h-60 rounded-lg overflow-hidden border border-gray-200">
              <GoogleMap
                mapContainerStyle={{width: '100%', height: '100%', position: 'relative', zIndex: 1}}
                center={{lat: report.latitude, lng: report.longitude}}
                zoom={15}
                onLoad={(map) => {
                  const color = report.userId === user?.id ? '#3b82f6' : '#10b981';
                  const size = 52;
                  new google.maps.Marker({
                    position: {lat: report.latitude, lng: report.longitude},
                    map: map,
                    title: `Signalement: ${report.title}`,
                    icon: {
                      url: 'data:image/svg+xml;utf-8,' + encodeURIComponent(`
                        <svg width="${size}" height="${size}" viewBox="0 0 24 28" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="12" cy="11" r="3" stroke="white" stroke-width="1.5" fill="${color}"/>
                          <path d="M21 10.8889C21 15.7981 15.375 22 12 22C8.625 22 3 15.7981 3 10.8889C3 5.97969 7.02944 2 12 2C16.9706 2 21 5.97969 21 10.8889Z" stroke="white" stroke-width="1.5" fill="${color}"/>
                          <text x="12" y="14" text-anchor="middle" font-size="10" fill="white">🐗</text>
                        </svg>
                      `),
                      scaledSize: new google.maps.Size(size, size),
                      anchor: new google.maps.Point(size / 2, size - 6),
                    }
                  });
                }}
                options={{
                  streetViewControl: false,
                  mapTypeControl: false,
                  fullscreenControl: false,
                  zoomControl: true,
                }}
              >
              </GoogleMap>

              {/* Bouton zoom */}
              <div className="absolute bottom-3 right-3">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleMapClick}
                  className="gap-2 bg-white/90 hover:bg-white"
                >
                  <ExternalLink className="w-3 h-3" />
                  Voir en grand
                </Button>
              </div>
            </div>
          </div>
        </SoftCard>

        {/* Informations légales */}
        <SoftCard className="p-4 bg-gray-50/50">
          <div className="text-xs text-muted-foreground space-y-2">
            <p>
              <strong>Information publique:</strong> Ce signalement est accessible publiquement dans le cadre de la prévention des risques liés à la faune sauvage.
            </p>
            <p>
              Signalement créé le {new Date(report.date).toLocaleDateString('fr-FR')}. Pour toute question, contactez les autorités compétentes.
            </p>
          </div>
        </SoftCard>
      </div>

      {/* Popup de confirmation de suppression */}
      <Dialog open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
        <DialogContent className="sm:max-w-md bg-white border-0 shadow-2xl rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Confirmer la suppression
            </DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer ce signalement ?
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <div className="bg-gray-50 p-4 rounded-xl border-0 shadow-sm">
              <h4 className="font-medium text-gray-900">{report.title}</h4>
              <p className="text-sm text-gray-600 mt-1">
                Signalement du {new Date(report.date).toLocaleDateString('fr-FR')}
              </p>
            </div>
            <p className="text-sm text-red-600 mt-3 font-medium">
              ⚠️ Cette action est irréversible
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteConfirmOpen(false)}
              className="border-gray-200 shadow-sm rounded-xl"
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 border-0 shadow-sm rounded-xl"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer définitivement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Popup photo fullscreen */}
      {report.photoUrl && (
        <Dialog open={isPhotoFullscreenOpen} onOpenChange={setIsPhotoFullscreenOpen}>
          <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 bg-black">
            <div className="relative w-full h-full flex items-center justify-center min-h-[50vh]">
              <img
                src={report.photoUrl}
                alt="Photo du signalement - Taille complète"
                className="max-w-full max-h-full object-contain"
                style={{maxHeight: '90vh'}}
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsPhotoFullscreenOpen(false)}
                className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 text-white"
              >
                <ExternalLink className="w-4 h-4" />
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}